import { useCallback, useRef, useState } from 'react'
import { Settings } from 'lucide-react'
import type { League, LeagueJoinPolicy, LeagueJoinRequest, Rival } from '../types'
import type { DetailEntryState } from '../useSharedElementTransition'
import RivalsList from './RivalsList'
import LeagueDetailHeader from './leagueDetail/LeagueDetailHeader'
import LeagueSettingsMenu from './leagueDetail/LeagueSettingsMenu'
import LeagueStandings from './leagueDetail/LeagueStandings'
import LeagueStickyBar from './leagueDetail/LeagueStickyBar'
import LeaveLeagueConfirm from './leagueDetail/LeaveLeagueConfirm'
import MottoEditorModal from './leagueDetail/MottoEditorModal'
import {
  formatMetaLine,
  leagueDetailRootClass,
} from './leagueDetail/leagueDetailFormat'
import { useStickyCollapse } from './leagueDetail/useStickyCollapse'
import './LeagueDetail.css'

interface LeagueDetailProps {
  league: League
  rivals: Rival[]
  onBack: () => void
  onInviteToLeague: (leagueId: string, userId: string) => void
  onLeaveLeague: (leagueId: string) => void
  onViewProfile: (userId: string, displayName: string) => void
  onToast: (message: string) => void
  // Admin-only motto save. Passing an empty string clears it. Omitted in contexts
  // that don't support editing (e.g. tests) — the edit/add affordances only render when present.
  onSaveMotto?: (leagueId: string, motto: string) => Promise<void> | void
  // Owner-only join requests and the who-can-join setting. Both are omitted in
  // contexts with no server behind them (tests, the rules demo) — the settings menu then shows
  // the policy as read from the league and no request section.
  joinRequests?: LeagueJoinRequest[]
  onAcceptJoinRequest?: (requestId: string) => void
  onDeclineJoinRequest?: (requestId: string) => void
  onChangeJoinPolicy?: (joinPolicy: LeagueJoinPolicy) => void
  // When set, the season hasn't opened yet: the hero rank shows an en dash and no
  // GW delta renders. null/undefined = season underway.
  seasonStartLabel?: string | null
  // Enter/exit transition wiring. When absent the route renders normally (deep link
  // / opened from a profile). When present, `state` drives the header-name hide/reveal and the
  // content stagger, and `nameRef` exposes the header name node so the shared-element flight can
  // measure its destination rect.
  entryTransition?: {
    state: DetailEntryState
    nameRef: (el: HTMLElement | null) => void
  }
}

function LeagueDetail({
  league,
  rivals,
  onBack,
  onLeaveLeague,
  onInviteToLeague,
  onViewProfile,
  onToast,
  onSaveMotto,
  joinRequests,
  onAcceptJoinRequest,
  onDeclineJoinRequest,
  onChangeJoinPolicy,
  seasonStartLabel,
  entryTransition,
}: LeagueDetailProps) {
  const pendingRequests = joinRequests ?? []
  const [isConfirmingLeave, setIsConfirmingLeave] = useState(false)
  // Admin settings menu. Owner-only: holds the invite code and the delete entry point,
  // both of which used to sit in the page body below the standings.
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isEditingMotto, setIsEditingMotto] = useState(false)
  const settingsTriggerRef = useRef<HTMLButtonElement>(null)
  const closeSettings = useCallback(() => setIsSettingsOpen(false), [])

  const { isCollapsed, isUserRowVisible, heroRankRef, youRowRef, jumpToUserRow } =
    useStickyCollapse()

  const isOwner = Boolean(league.inviteCode)
  // The motto is admin-authored: only the league owner can add/edit it, and only when a save
  // handler is wired. Non-admins never see an editing prompt they can't act on.
  const canEditMotto = isOwner && Boolean(onSaveMotto)
  const isMini = league.type === 'mini'
  const isSeasonOpener = Boolean(seasonStartLabel)

  const you = league.standings.find((m) => m.isYou)
  const rankDelta = you?.movementDelta ?? null
  const gapToAbove = you?.gapToAbove ?? null

  return (
    <div className={leagueDetailRootClass(entryTransition?.state)}>
      {/* The sticky bar summarises the "you" row and jumps to it, so it only exists when there is
          one. */}
      {you && (
        <LeagueStickyBar
          leagueName={league.name}
          yourRank={league.yourRank}
          yourPoints={you.points ?? 0}
          rankDelta={rankDelta}
          gapToAbove={gapToAbove}
          isSeasonOpener={isSeasonOpener}
          isCollapsed={isCollapsed}
          isUserRowVisible={isUserRowVisible}
          onJump={jumpToUserRow}
        />
      )}

      {/* Top row: back left, admin settings right. Both scroll away with the page —
          the sticky collapsed bar above is a separate element and carries neither. */}
      <div className="league-detail__topbar">
        <button
          className="league-detail__back"
          onClick={onBack}
          aria-label="Back to leagues"
        >
          <span className="league-detail__back-chevron" aria-hidden="true">
            ‹
          </span>
          Leagues
        </button>

        {isMini && isOwner && (
          <button
            type="button"
            ref={settingsTriggerRef}
            className="league-detail__settings-btn"
            onClick={() => setIsSettingsOpen(true)}
            aria-label={
              pendingRequests.length > 0
                ? `League settings, ${pendingRequests.length} join requests`
                : 'League settings'
            }
            aria-haspopup="dialog"
            aria-expanded={isSettingsOpen}
          >
            {/* size is set via font-size on .league-detail__settings-btn — the .lucide CSS
                rule overrides this prop's width/height attributes anyway. */}
            <Settings aria-hidden="true" />
            {/* Without this the owner has to open the menu to discover anyone is waiting. */}
            {pendingRequests.length > 0 && (
              <span className="league-detail__settings-badge">{pendingRequests.length}</span>
            )}
          </button>
        )}
      </div>

      <LeagueDetailHeader
        leagueName={league.name}
        motto={league.motto}
        canEditMotto={canEditMotto}
        metaLine={formatMetaLine(league.memberCount, league.estYear)}
        yourRank={league.yourRank}
        yourPoints={you?.points ?? 0}
        rankDelta={rankDelta}
        yourGwPoints={league.yourGwPoints}
        isSeasonOpener={isSeasonOpener}
        onEditMotto={() => setIsEditingMotto(true)}
        nameRef={entryTransition?.nameRef}
        heroRankRef={heroRankRef}
      />

      <LeagueStandings
        standings={league.standings}
        onViewProfile={onViewProfile}
        youRowRef={youRowRef}
      />

      {/* Non-owners only: the owner's invite code moved into the settings menu, so all
          that's left in this slot is the line telling a member how to get people in. The member list
          above is always boxed, so this block takes the flush treatment. */}
      {isMini && !isOwner && (
        <div className="league-detail__invite-code section-flush">
          <p className="league-detail__ask-owner">
            Ask the league owner to invite you.
          </p>
        </div>
      )}

      {/* `Leave league` stays in the body for members; the owner's `Delete league` moved into the
          settings menu. */}
      {isMini && !isOwner && (
        <button
          className="league-detail__leave-btn"
          onClick={() => setIsConfirmingLeave(true)}
        >
          Leave league
        </button>
      )}

      {isMini && (
        <RivalsList
          rivals={rivals}
          standings={league.standings}
          onInviteToLeague={(userId) => onInviteToLeague(league.id, userId)}
          onViewProfile={onViewProfile}
          toggleLabel="Invite players"
          emptyMessage="No rivals to invite yet — challenge someone from their profile first."
          showRecord={false}
        />
      )}

      {isSettingsOpen && (
        <LeagueSettingsMenu
          inviteCode={league.inviteCode}
          joinPolicy={league.joinPolicy}
          onChangeJoinPolicy={(policy) => onChangeJoinPolicy?.(policy)}
          joinRequests={pendingRequests}
          onAcceptJoinRequest={(requestId) => onAcceptJoinRequest?.(requestId)}
          onDeclineJoinRequest={(requestId) => onDeclineJoinRequest?.(requestId)}
          triggerRef={settingsTriggerRef}
          onClose={closeSettings}
          onRequestDelete={() => {
            setIsSettingsOpen(false)
            setIsConfirmingLeave(true)
          }}
          onToast={onToast}
        />
      )}

      {isEditingMotto && onSaveMotto && (
        <MottoEditorModal
          leagueName={league.name}
          motto={league.motto}
          onSave={(motto) => onSaveMotto(league.id, motto)}
          onClose={() => setIsEditingMotto(false)}
        />
      )}

      {isConfirmingLeave && (
        <LeaveLeagueConfirm
          isOwner={isOwner}
          onConfirm={() => {
            setIsConfirmingLeave(false)
            onLeaveLeague(league.id)
          }}
          onClose={() => setIsConfirmingLeave(false)}
        />
      )}
    </div>
  )
}

export default LeagueDetail
